import React, { useEffect, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { Loader2, Users, Send, CheckCircle2, XCircle, Activity, ChevronLeft, ChevronRight, Filter, ListFilter, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AnalyticsData {
	leads: {
		total: number;
		never_visited: number;
		qualified: number;
		rejected: number;
		in_progress: number;
	};
	le_status: {
		sent: number;
		rejected: number;
		accepted: number;
	};
	filters: {
		baps: { id: number; name: string }[];
		lenders: { id: number; name: string }[];
	};
}

const LEAD_COLORS = ["#94a3b8", "#10b981", "#f59e0b", "#ef4444"];
const LE_COLORS = ["#3b82f6", "#10b981", "#ef4444"];

const AdminAnalytics = () => {
	const [data, setData] = useState<AnalyticsData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeChartIndex, setActiveChartIndex] = useState(0);

	const [bapId, setBapId] = useState<string>("");
	const [leId, setLeId] = useState<string>("");
	const [isFilterExpanded, setIsFilterExpanded] = useState(false);

	useEffect(() => {
		if (data?.filters?.baps?.length === 1 && !bapId) {
			setBapId(String(data.filters.baps[0].id));
		}
	}, [data, bapId]);

	useEffect(() => {
		const fetchAnalytics = async () => {
			setLoading(true);
			try {
				const params = new URLSearchParams();
				if (bapId) params.append('bap_id', bapId);
				if (leId) params.append('le_id', leId);

				const res = await axios.get(`${API_BASE_URL}/api/admin/analytics/?${params.toString()}`);
				setData(res.data);
			} catch (err) {
				setError("Failed to load analytics data");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		fetchAnalytics();
	}, [bapId, leId]);

	if (loading && !data) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
				{error || "No data available"}
			</div>
		);
	}

	const leadChartData = [
		{ name: "Never Visited", value: data.leads.never_visited },
		{ name: "Qualified", value: data.leads.qualified },
		{ name: "In Progress", value: data.leads.in_progress },
		{ name: "Rejected", value: data.leads.rejected },
	].filter(item => item.value > 0);

	const leChartData = [
		{ name: "Sent (Pending)", value: Math.max(0, data.le_status.sent - data.le_status.accepted - data.le_status.rejected) },
		{ name: "Accepted", value: data.le_status.accepted },
		{ name: "Rejected", value: data.le_status.rejected },
	].filter(item => item.value > 0);

	const charts = [
		{
			title: `Lead Journey (Total: ${data.leads.total})`,
			icon: <Users className="h-5 w-5 text-intellend-green" />,
			content: (
				<section className="animate-in fade-in slide-in-from-right-4 duration-500">
					<div className="grid gap-8 lg:grid-cols-2 items-start">
						<div className="grid gap-2 grid-cols-2 lg:gap-4 order-2 lg:order-1">
							<Card className="border-l-4 border-l-gray-400 shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<CardDescription className="text-xs sm:text-sm">Never Visited</CardDescription>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.leads.never_visited}</CardTitle>
								</CardHeader>
							</Card>
							<Card className="border-l-4 border-l-intellend-green shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<CardDescription className="text-xs sm:text-sm">Qualified for LE</CardDescription>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.leads.qualified}</CardTitle>
								</CardHeader>
							</Card>
							<Card className="border-l-4 border-l-intellend-gold shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<CardDescription className="text-xs sm:text-sm">In Progress</CardDescription>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.leads.in_progress}</CardTitle>
								</CardHeader>
							</Card>
							<Card className="border-l-4 border-l-red-500 shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<CardDescription className="text-xs sm:text-sm">Rejected</CardDescription>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.leads.rejected}</CardTitle>
								</CardHeader>
							</Card>
						</div>

						<Card className="p-6 shadow-sm border-gray-200 h-[400px] flex flex-col order-1 lg:order-2">
							<CardHeader className="px-0 pt-0 pb-4">
								<CardTitle className="text-base font-semibold text-center">Lead Distribution</CardTitle>
							</CardHeader>
							<div className="flex-1 w-full">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={leadChartData}
											cx="50%"
											cy="50%"
											innerRadius={60}
											outerRadius={100}
											paddingAngle={5}
											dataKey="value"
										>
											{leadChartData.map((entry, index) => {
												let color = "#cbd5e1";
												if (entry.name === "Qualified") color = "#10b981";
												if (entry.name === "In Progress") color = "#f59e0b";
												if (entry.name === "Rejected") color = "#ef4444";
												if (entry.name === "Never Visited") color = "#94a3b8";

												return <Cell key={`cell-${index}`} fill={color} />;
											})}
										</Pie>
										<Tooltip
											contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
										/>
										<Legend verticalAlign="bottom" height={36} />
									</PieChart>
								</ResponsiveContainer>
							</div>
						</Card>
					</div>
				</section>
			)
		},
		{
			title: "Lending Entity (LE) Outcomes",
			icon: <Activity className="h-5 w-5 text-blue-600" />,
			content: (
				<section className="animate-in fade-in slide-in-from-right-4 duration-500">
					<div className="grid gap-8 lg:grid-cols-2 items-center">
						<div className="grid gap-2 grid-cols-2 lg:grid-cols-1 lg:gap-4 order-2 lg:order-1">
							<Card className="border-l-4 border-l-blue-500 shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<div className="flex items-center gap-1.5 mb-0.5 sm:mb-1">
										<Send className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500" />
										<CardDescription className="m-0 text-xs sm:text-sm">Sent to LE</CardDescription>
									</div>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.le_status.sent}</CardTitle>
								</CardHeader>
							</Card>
							<Card className="border-l-4 border-l-intellend-green shadow-sm">
								<CardHeader className="p-3 sm:p-4">
									<div className="flex items-center gap-1.5 mb-0.5 sm:mb-1">
										<CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-intellend-green" />
										<CardDescription className="m-0 text-xs sm:text-sm">Accepted</CardDescription>
									</div>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.le_status.accepted}</CardTitle>
								</CardHeader>
							</Card>
							<Card className="border-l-4 border-l-red-500 shadow-sm col-span-2 sm:col-span-1">
								<CardHeader className="p-3 sm:p-4">
									<div className="flex items-center gap-1.5 mb-0.5 sm:mb-1">
										<XCircle className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
										<CardDescription className="m-0 text-xs sm:text-sm">Rejected</CardDescription>
									</div>
									<CardTitle className="text-2xl sm:text-3xl font-bold">{data.le_status.rejected}</CardTitle>
								</CardHeader>
							</Card>
						</div>

						<Card className="p-6 shadow-sm border-gray-200 h-[400px] flex flex-col order-1 lg:order-2">
							<CardHeader className="px-0 pt-0 pb-4">
								<CardTitle className="text-base font-semibold text-center">Conversion Funnel</CardTitle>
							</CardHeader>
							<div className="flex-1 w-full">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={leChartData}
											cx="50%"
											cy="50%"
											innerRadius={60}
											outerRadius={100}
											paddingAngle={5}
											dataKey="value"
										>
											{leChartData.map((entry, index) => (
												<Cell key={`cell-${index}`} fill={LE_COLORS[index % LE_COLORS.length]} />
											))}
										</Pie>
										<Tooltip
											contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
										/>
										<Legend verticalAlign="bottom" height={36} />
									</PieChart>
								</ResponsiveContainer>
							</div>
						</Card>
					</div>
				</section>
			)
		}
	];

	const nextChart = () => setActiveChartIndex((prev) => (prev + 1) % charts.length);
	const prevChart = () => setActiveChartIndex((prev) => (prev - 1 + charts.length) % charts.length);

	return (
		<div className="space-y-6 max-w-full overflow-hidden pb-8">
			{/* Carousel Header / Name Box */}
			<div className="flex items-center justify-between bg-white p-2 sm:p-4 rounded-xl shadow-sm border border-gray-100 mb-2 relative">
				{loading && (
					<div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-xl">
						<Loader2 className="h-5 w-5 animate-spin text-intellend-green" />
					</div>
				)}
				<Button
					variant="outline"
					size="icon"
					onClick={prevChart}
					className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full shadow-md border-gray-200 bg-white hover:bg-intellend-green hover:text-white hover:border-intellend-green transition-all duration-300 group"
				>
					<ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 group-hover:text-white" />
				</Button>

				<div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 bg-intellend-green/5 rounded-full border border-intellend-green/20 shadow-inner flex-1 min-w-0 justify-center">
					<div className="shrink-0">{charts[activeChartIndex].icon}</div>
					<h3 className="text-sm sm:text-lg font-bold text-gray-900 truncate">
						{charts[activeChartIndex].title}
					</h3>
				</div>

				<Button
					variant="outline"
					size="icon"
					onClick={nextChart}
					className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full shadow-md border-gray-200 bg-white hover:bg-intellend-green hover:text-white hover:border-intellend-green transition-all duration-300 group"
				>
					<ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 group-hover:text-white" />
				</Button>
			</div>

			{/* Filter Toggle Button */}
			{!isFilterExpanded && (
				<div className="flex justify-center -mt-4 relative z-10">
					<Button
						variant="outline"
						size="icon"
						onClick={() => setIsFilterExpanded(true)}
						className="h-10 w-10 rounded-full shadow-lg border-intellend-green/20 text-intellend-green bg-white hover:bg-intellend-green/5 hover:border-intellend-green transition-all duration-300 relative group"
					>
						<Filter className="h-5 w-5" />
						<span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
							Filter Data
						</span>
					</Button>
				</div>
			)}

			{/* Filters */}
			{isFilterExpanded && (
				<div className="bg-white p-6 rounded-xl shadow-md border border-intellend-green/10 animate-in fade-in slide-in-from-top-4 duration-300 relative">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setIsFilterExpanded(false)}
						className="absolute top-2 right-2 h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
					>
						<X className="h-5 w-5" />
					</Button>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-2">
						<div className="space-y-2">
							<label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
								<ListFilter className="h-4 w-4 text-intellend-green" />
								Filter by BAP Name
							</label>
							<select
								value={bapId}
								onChange={(e) => setBapId(e.target.value)}
							disabled={data.filters.baps.length === 1}
							className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-intellend-green/20 focus:border-intellend-green transition-all text-sm"
						>
							{data.filters.baps.length > 1 && (
								<option value="">All BAPs</option>
							)}
								{data.filters.baps.map(bap => (
									<option key={bap.id} value={bap.id}>{bap.name}</option>
								))}
							</select>
						</div>
						<div className="space-y-2">
							<label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
								<ListFilter className="h-4 w-4 text-intellend-green" />
								Filter by Lending Entity (LE)
							</label>
							<select
								value={leId}
								onChange={(e) => setLeId(e.target.value)}
								className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-intellend-green/20 focus:border-intellend-green transition-all text-sm"
							>
								<option value="">All Lenders</option>
								{data.filters.lenders.map(le => (
									<option key={le.id} value={le.id}>{le.name}</option>
								))}
							</select>
						</div>
					</div>
				</div>
			)}

			{/* Chart Content */}
			<div className="relative">
				{charts[activeChartIndex].content}
			</div>

			{/* Dots Indicator */}
			<div className="flex justify-center gap-2 mt-4">
				{charts.map((_, idx) => (
					<div
						key={idx}
						className={`h-2 w-2 rounded-full transition-all duration-300 ${activeChartIndex === idx ? "bg-intellend-green w-6" : "bg-gray-300"
							}`}
					/>
				))}
			</div>
		</div>
	);
};

export default AdminAnalytics;
